import React, { useContext, useEffect, useMemo, useState } from 'react'
import refundImg from '../../../../assets/images/refund.png'
import CheckboxWithLabelForForm from '../../ui/CheckboxWithLabelForForm'
import Button from '../../ui/Button'
import { AdminContext } from '../../../contexts/AdminContext'
import api from '../../../api2'
import { ModalContext } from '../../../contexts/admin/ModalContext'
import InputFieldWithCurrency from '../../Admin/Inputs/InputFieldWithCurrency'
import TextAreaWithLabel from '../../Admin/Inputs/TextAreaWithLabel'
import { useDebouncedCallback } from 'use-debounce'
import ProcessingState from '../../ui/Admin/ProcessingState'
import twoDecimalPlaces from '../../../utils/twoDecimalPlaces'
import RefundOptionSelector from './RefundOptionsSelector'
import { REFUND_FILTER_OPTIONS, refundFilterOptions } from './RefundOptions'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import HoverTip from '../../ui/HoverTip'
import RefundOrderSummary from './RefundOrderSummary'
import IPayment from '../../../interfaces/IPayment'
import { set } from 'lodash'
import ePaymentMode from '~/enums/ePaymentMode'
import IOrder from '~/interfaces/IOrder'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { zeroIfNegative } from '~/utils/zeroIfNegative'

interface IComponentProps {
  payment: IPayment
  initialAmount: number
  externalUrl: string | undefined
  order?: IOrder
}

const RefundModalContents = ({ payment, initialAmount, externalUrl, order }: IComponentProps) => {
  const { onClose } = useContext(ModalContext)
  const { camp, showNotification } = useContext(AdminContext)
  const [isRefundingServiceFee, setIsRefundingServiceFee] = useState(false)
  const [reason, setReason] = useState('')
  const [isLoading, setIsLoading] = useState<boolean>()
  const [showSuccess, setShowSuccess] = useState<boolean>()
  const isOfflinePayment = [ePaymentMode.MODE_OFFLINE, ePaymentMode.MODE_MANUAL_UPDATE].includes(payment.mode)
  const dynamicRefundFilterOptions = isOfflinePayment ? refundFilterOptions.slice(1) : refundFilterOptions

  const refunded = useMemo(
    () => payment.refunds?.reduce((a, b) => a + b.amount_in_cents, 0) || 0,
    [payment.refunds]
  )

  const isUnpaidNegative = useMemo(() => {
    const unpaid = order?.unpaid_total_in_cents ?? 0

    return (unpaid < 0 && !refunded) || (!!refunded && refunded < Math.abs(unpaid))
  }, [order, payment, refunded])
  
  const [selectedFilterOption, setSelectedFilterOption] = useState(
    isUnpaidNegative
      ? dynamicRefundFilterOptions.find((opt) => opt.value === REFUND_FILTER_OPTIONS.CUSTOM) ||
          dynamicRefundFilterOptions[0]
      : dynamicRefundFilterOptions[0],
  )
  const [refundAmount, setRefundAmount] = useState(initialAmount)
  const [hasUserModifiedRefund, setHasUserModifiedRefund] = useState(false)

  useEffect(() => {
    const amountOwed = order?.unpaid_total_in_cents ?? 0
    const paymentAmount = payment.amount_in_cents
    const refundsExist = (payment.refunds?.length ?? 0) > 0
    const alreadyRefundedAmount = payment.refunds?.reduce((a, b) => a + b.amount_in_cents, 0) ?? 0
    const noRefunds = !refundsExist

    let calculatedRefundAmount = paymentAmount

    if (amountOwed < 0 && noRefunds) {
      calculatedRefundAmount = Math.abs(amountOwed)
    } else if (refundsExist && alreadyRefundedAmount < Math.abs(amountOwed)) {
      calculatedRefundAmount = Math.abs(amountOwed) - alreadyRefundedAmount
    } else if (refundsExist) {
      calculatedRefundAmount = paymentAmount - alreadyRefundedAmount
    } else if (noRefunds) {
      calculatedRefundAmount = paymentAmount
    }

    setRefundAmount(calculatedRefundAmount / 100)
  }, [])

  const debouncedRefund = useDebouncedCallback(() => {
    api.Payments.refund(payment.id, {
      amountInCents: Number(refundAmount) * 100.0,
      reason: reason,
      isRefundingServiceFee: isRefundingServiceFee,
    })
      .then(() => {
        setShowSuccess(true)
        setTimeout(() => {
          onClose()
        }, 2000)
      })
      .catch((err) => {
        showNotification({
          type: 'error',
          title: 'Failed to refund payment',
          description: err.message,
          dismissAfter: 5000,
        })
        console.error(err)
        setIsLoading(false)
      })
  }, 100)

  useEffect(() => {
    const total = payment.amount_in_cents
    const appFee = payment.application_fee_in_cents
    const stripeFee = payment.stripe_fee_in_cents

    let newRefundAmount = 0

    switch (selectedFilterOption.value) {
      case "net": {
        const net = total - appFee - stripeFee
        if (isRefundingServiceFee) {
          const denominator = 1 - (appFee / (total || 1))
          const grossToRefund = denominator !== 0 ? net / denominator : 0
          newRefundAmount = (grossToRefund - refunded) / 100
        } else {
          newRefundAmount = (net - refunded) / 100
        }
        break
      }
      case "full":
        newRefundAmount = (total - refunded) / 100
        break
      default:
        return
    }

    setRefundAmount(zeroIfNegative(Number(twoDecimalPlaces(newRefundAmount))))
  }, [selectedFilterOption, isRefundingServiceFee])

  const handleCancel = () => {
    onClose()
  }

  const handleRefund = () => {
    setIsLoading(true)
    debouncedRefund()
  }

  const isRefundButtonDisabled = isLoading || !refundAmount || refundAmount <= 0

  const handleRefundAmountChange = (e) => {
    setRefundAmount(e.target.value)
    setSelectedFilterOption(refundFilterOptions[2])
    setHasUserModifiedRefund(true)
  }

  const handleFilterOptionChange = (option: typeof selectedFilterOption) => {
    setSelectedFilterOption(option)
    setHasUserModifiedRefund(true)
  }

  if (isLoading) {
    return (
      <ProcessingState
        loadingMessages={[
          'Processing refund...',
          'Contacting payment processor...',
          "Dotting i's, crossing t's...",
          'Almost finished...',
        ]}
        successMessage={showSuccess ? 'Refund successful' : undefined}
      />
    )
  }

  return (
    <div className='flex items-start gap-3'>
      <img src={refundImg} className='hidden xs:block' />
      <div className='flex flex-col'>
        <div className='mb-5'>
          <div className='font-semibold text-lg mb-1.5'>Refund payment</div>
          {isOfflinePayment ? (
            <div className='space-y-2'>
              <p>
                Payments collected outside of Stripe must be refunded externally. Offline refunds
                recorded here will be reflected in your reports.
              </p>
            </div>
          ) : (
            <div className=''>
              Refunds take 5-10 days to appear on a customer's statement. Stripe's fees for the
              original payment won't be returned.
            </div>
          )}
        </div>
        <div className='mb-3 flex flex-col justify-start'>
          <RefundOptionSelector
            options={dynamicRefundFilterOptions}
            selectedOption={selectedFilterOption}
            onChange={handleFilterOptionChange}
          />
          {isUnpaidNegative && !hasUserModifiedRefund && (
            <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded px-3 py-2 mb-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-orange-700">
                The refund amount has been set automatically based on the amount due. Please confirm this amount before issuing a refund.
              </span>
            </div>
          )}
          <InputFieldWithCurrency
            value={refundAmount}
            onChange={handleRefundAmountChange}
            currency={camp.currency}
            disabled={isLoading}
            label='Refund'
          />
          {!isOfflinePayment && (
            <div className='mt-4 flex'>
              <CheckboxWithLabelForForm
                label={`Refund the service fee`}
                value={isRefundingServiceFee}
                setValue={setIsRefundingServiceFee}
                disabled={isLoading}
                labelClasses='text-xs font-semibold'
              />
              <HoverTip
                content={
                  "When checked, an amount of Park's service fee proportional to the refund amount will be drawn from our account to contribute to the refund."
                }
                width='w-96'
                white>
                <InformationCircleIcon className='w-3.5 h-3.5 ml-0.5' />
              </HoverTip>
            </div>
          )}
          <RefundOrderSummary
            paymentTotal={payment.amount_in_cents}
            parkApplicationFees={payment.application_fee_in_cents}
            stripeProcessingFees={payment.stripe_fee_in_cents}
            yourNet={
              payment.amount_in_cents -
              payment.application_fee_in_cents -
              payment.stripe_fee_in_cents
            }
            yourUpdatedNet={
              isRefundingServiceFee
                ? payment.amount_in_cents -
                  (payment.stripe_fee_in_cents + payment.application_fee_in_cents) +
                  ((refundAmount * 100) / payment.amount_in_cents) *
                    payment.application_fee_in_cents -
                  refundAmount * 100
                : payment.amount_in_cents -
                  payment.application_fee_in_cents -
                  payment.stripe_fee_in_cents -
                  refundAmount * 100
            }
            camp={camp}
            externalUrl={externalUrl}
            isOfflinePayment={isOfflinePayment}
          />
        </div>
        <div className=''>
          <TextAreaWithLabel
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={isLoading}
            label='Reason for refund (optional - for internal use only)'
          />
        </div>
        <div className='flex justify-end gap-3 mt-6'>
          <Button
            variant='none'
            className='border border-gray-350 text-gray-500 hover:bg-gray-300 hover:text-gray-600'
            children={'Cancel'}
            disabled={isLoading}
            onClick={handleCancel}
          />
          <Button variant='blue' children={'Refund'} disabled={isRefundButtonDisabled} onClick={handleRefund} />
        </div>
      </div>
    </div>
  )
}

export default RefundModalContents
